import { ApolloClient, ApolloProvider, split } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { Query } from '@apollo/client/react/components';
import { getMainDefinition } from '@apollo/client/utilities';
import { CachePersistor } from 'apollo-cache-persist';
import { createUploadLink } from 'apollo-upload-client';
import { createClient } from 'graphql-ws';
import { get } from 'lodash';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { INITIALIZE_WEBCHAT } from './actions/Webchat';
import possibleTypes from './assets/fragmentTypes.json';
import InteractContainer from './components/InteractContainer';
import ConfigProvider from './ConfigProvider';
import ChatUserReceiver from './utils/ChatUserReceiver';
import { setContext } from '@apollo/client/link/context';

const base64ToObject = base64 => {
  try {
    return JSON.parse(atob(base64));
  } catch (e) {
    return { ERROR: 'JSON parse failed' };
  }
};

const getPayloadInfo = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const queryPayload = searchParams.get('payload') || searchParams.get('config');
  const hashPayload = window.location.hash ? window.location.hash.replace(/^#/, '') : '';
  const pathPayload = window.location.pathname ? window.location.pathname.replace(/^\/+/, '') : '';

  const candidates = [
    { payload: queryPayload, source: 'query' },
    { payload: hashPayload, source: 'hash' },
    { payload: pathPayload, source: 'path' },
  ];

  let selected = { payload: '', source: 'none', obj: {} };

  for (const candidate of candidates) {
    if (!candidate.payload) continue;
    const obj = base64ToObject(candidate.payload);
    if (obj && !obj.ERROR && obj.channelId) {
      selected = { ...candidate, obj };
      return selected;
    }
    if (!selected.payload) {
      selected = { ...candidate, obj };
    }
  }

  return selected;
};

const { obj: urlObj } = getPayloadInfo();

const SESSION_DATA = urlObj.session;
const CHANNEL_ID = urlObj.channelId;
const hasNotifications = urlObj.hasNotifications;
const UPLOAD_ENABLED = !!urlObj.u;
const chatUserId = urlObj.c;
const slug = urlObj.slug;
const sessionId = urlObj.s;
const welcomeMessage = urlObj.welcomeMessage;
const allowRestartConversation = urlObj.allowRestartConversation;
const openOnFirstTime = urlObj.openOnFirstTime;

const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_GRAPHQL_WS,
  })
);

const authLink = setContext((_, { headers }) => {
  const token = SESSION_DATA && SESSION_DATA.authToken;
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const httpLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL,
});

const link = split(
  // split based on operation type
  ({ query }) => {
    const { kind, operation } = getMainDefinition(query);
    return kind === 'OperationDefinition' && operation === 'subscription';
  },
  wsLink,
  authLink.concat(httpLink)
);

const cache = new InMemoryCache({
  possibleTypes,
});

const client = new ApolloClient({
  link,
  cache,
  connectToDevTools: true,
});

// const width = urlObj.w;
// const height = urlObj.h;

const persistor = new CachePersistor({
  cache,
  storage: window.localStorage,
  key: CHANNEL_ID,
});

// Component that conditionally renders ChatUserReceiver only when chat is open
const ChatWrapper = ({ data, subscribeToMore }) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [hasInitializedChat, setHasInitializedChat] = useState(false);

  // Send LOADED message when component mounts to show button
  useEffect(() => {
    window.parent.postMessage(JSON.stringify({ action: 'LOADED' }), '*');
  }, []);

  const autoOpen = get(data.theme, 'action.autoOpen');
  useEffect(() => {
    console.log('autoOpen: ', autoOpen);
    if (autoOpen) {
      console.log('posting "open" message to parent');
      window.parent.postMessage(JSON.stringify({ action: 'open' }), '*');
    }
  }, [autoOpen]);

  // Listen for chat open/close messages from parent
  useEffect(() => {
    const messageHandler = event => {
      try {
        const parsedData = JSON.parse(event.data);
        if (parsedData.action === 'chat_open') {
          console.log('setting chat open');
          setChatOpen(true);
          if (!hasInitializedChat) {
            setHasInitializedChat(true);
          }
        }
        if (parsedData.action === 'chat_close') {
          setChatOpen(false);
        }
      } catch (e) {
        // Ignore invalid messages
      }
    };

    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, [hasInitializedChat]);

  console.log('is chat open::', chatOpen);
  console.log('has initialized chat::', hasInitializedChat);

  // Only render ChatUserReceiver when chat has been opened at least once
  if (!hasInitializedChat) {
    console.log('WebChat client loaded, waiting for chat to open...');
    return null;
  }

  return (
    <ChatUserReceiver
      slug={slug}
      session={SESSION_DATA}
      persistor={persistor}
      channelId={CHANNEL_ID}
      chatUserId={chatUserId}
      subscribeToMore={subscribeToMore}
      saveHistory={get(data.theme, 'action.saveHistory')}
      chatOpen={chatOpen}
    >
      {({ user, isTyping, messagesFromSubscription }) => (
        <ConfigProvider
          theme={{ ...data.theme } || {}}
          user={user.id}
          channel={CHANNEL_ID}
          session={SESSION_DATA}
          hasNotifications={hasNotifications}
          enableUpload={UPLOAD_ENABLED}
          sessionId={sessionId}
          welcomeMessage={welcomeMessage}
          allowRestartConversation={allowRestartConversation}
          openOnFirstTime={openOnFirstTime}
        >
          <InteractContainer user={user} isTyping={isTyping} messagesFromSubscription={messagesFromSubscription} />
        </ConfigProvider>
      )}
    </ChatUserReceiver>
  );
};

persistor
  .restore()
  .then(() => {
    ReactDOM.render(
      <ApolloProvider client={client}>
        <Query query={INITIALIZE_WEBCHAT} variables={{ channelId: CHANNEL_ID }} fetchPolicy="network-only">
          {({ data, loading, error, subscribeToMore }) => {
            console.log('INITIALIZE_WEBCHAT - channelId:', CHANNEL_ID, 'loading:', loading, 'error:', error);
            if (loading) {
              return null;
            }
            if (error) {
              return console.log('Error::', error.message) || <span>ERROR:: {error.message}</span>;
            }
            if (!data) {
              return null;
            }

            return <ChatWrapper data={data} subscribeToMore={subscribeToMore} />;
          }}
        </Query>
      </ApolloProvider>,
      document.getElementById('root')
    );
  })
  .catch(e => {
    console.error('Something went wrong when restoring the cache');
  });
